import * as R from 'ramda'
import { t } from 'i18next'
import {
  ClientFrontlineConfig,
  CompletedOffer,
  EquipmentGroups,
  FrontlineConfigJson,
  Offer,
  Opportunity,
  Option
} from 'app/types/types'
import { CommercialTermKey, CommercialTerms, CommercialTermsWithPricing, EquipmentDate } from 'nemo-pricing'
import { getEquipmentContractDate, getEquipmentWiseDate } from 'app/app/common/common-utils'
import moment from 'moment'
import { isRenegoOpp } from 'app/app/common/opportunity'
import { CommercialTermRow } from 'app/types/tables'
import { getCommercialTermChoiceTranslation } from 'app/app/common/translations'
import { updateOfferAsync } from '../../redux/features/currentOffer/update-offer-async'
import { setOfferPriceEstimation } from '../../nemo/offer/price-estimation'
import { isExtraCommercialTerm } from '../../nemo/pricing/extra-services'
import { COMMERCIAL_TERMS_TO_FILTER, PRICING_IMPACT_TERMS } from '../client-constants'
import { getCommercialTermsErrors } from '../../nemo/offer'

const commercialTermsWithPricing: Record<keyof CommercialTermsWithPricing, boolean> = {
  KCSM_BILLING_PLAN: true,
  VKBUR: true,
  ZLSCH: true,
  ZTERM: true,
  VLAUFK: true,
  AUGRU: true,
  KCSM_INV_ATTACH: true,
  KCMA_PO_FOR_CALLOUTS: true,
  KCSM_PROJECT_TYPE: true,
  KCSM_DISTANCE_RANGE: true,
  KCSM_BILLING_MODE: true,
  KCMA_PO_FOR_PSR: true
}

export function isPaired(typeOrKey: string): boolean {
  return typeOrKey === 'pair-select' || typeOrKey.includes(':')
}

function pair(val1: string, val2: string): string {
  return val1 + ':' + val2
}

export function isKcsmEscalationDateFlexible(frontlineConfig: ClientFrontlineConfig) {
  return frontlineConfig.commercialTerms.some(
    term => term.key === 'KCSM_BASE_MONTH_ESCALATION' && term.type === 'flexible-date'
  )
}

export function isKcsmEscalationDateEnabled(frontlineConfig: ClientFrontlineConfig) {
  return frontlineConfig.commercialTerms.some(term => term.key === 'KCSM_BASE_MONTH_ESCALATION')
}

export function getEscalationFlowCommercialTerms(commercialTerms: CommercialTermRow[]) {
  const escalationFlowCommercialTermKeys = [
    'VBEGDAT',
    'VABNDAT',
    'VAT',
    'VAT_2',
    'VAT_3',
    'KONDA',
    'VLAUFK',
    'PROPORTIONAL_PRICE'
  ]
  return commercialTerms.filter(commercialTerm => {
    const { key } = commercialTerm
    return escalationFlowCommercialTermKeys.includes(key)
  })
}

export function getEscalationDateOption(frontlineConfig: ClientFrontlineConfig): '1' | '2' {
  return frontlineConfig.sap.escalationDate || '1'
}

export function getEscalationStartDate(frontlineConfig: ClientFrontlineConfig, contractStartDate: string) {
  if (contractStartDate === 'flexible') {
    return isKcsmEscalationDateFlexible(frontlineConfig)
      ? 'flexible'
      : isKcsmEscalationDateEnabled(frontlineConfig)
        ? undefined
        : 'flexible'
  }
  const escalationDateOption = getEscalationDateOption(frontlineConfig)
  return escalationDateOptions[escalationDateOption](contractStartDate)
}

export function getEscalationEquipmentStartDate(
  frontlineConfig: ClientFrontlineConfig,
  contractStartDate: EquipmentDate,
  offer: Offer
) {
  const eqContractDate = {}
  offer.buildings.forEach(({ details }) => {
    details.equipment.forEach(equipment => {
      const escalationDateOption = getEscalationDateOption(frontlineConfig)
      const equipmentStartDate = getEquipmentWiseDate(equipment.salesToolId, contractStartDate) || ''
      Object.assign(eqContractDate, {
        [equipment.salesToolId]: escalationDateOptions[escalationDateOption](equipmentStartDate)
      })
    })
  })
  return eqContractDate
}

interface EscalationDateOptions {
  '1': (contractStartDate: string) => string
  '2': (contractStartDate: string) => string
}

const escalationDateOptions: EscalationDateOptions = {
  '1': (contractStartDate: string) => moment(contractStartDate).locale('en').startOf('year').add(1, 'year').format('YYYY-MM-DD'),
  '2': (contractStartDate: string) => moment(contractStartDate).locale('en').add(1, 'year').format('YYYY-MM-DD')
}

export function getValue(
  commercialTerm: CommercialTermRow,
  currentOfferCommercialTerms: CommercialTerms,
  isTermPaired: boolean,
  isContractStartDateAtEquipmentLevel: boolean,
  offer: Offer
): string | string[] {
  const { key, pair: pairKey } = commercialTerm
  const first = currentOfferCommercialTerms[key]!
  const second = currentOfferCommercialTerms[pairKey as CommercialTermKey]!

  if (['VBEGDAT', 'VENDDAT', 'VABNDAT'].includes(key) && isContractStartDateAtEquipmentLevel) {
    return getEquipmentContractDate(first as EquipmentDate, key, offer as CompletedOffer) as string
  } else if (key === 'KCSM_BASE_MONTH_ESCALATION' && isContractStartDateAtEquipmentLevel) {
    return ''
  }

  return isTermPaired ? pair(first as string, second as string) : (first as string | string[]) // changes
}

export function unpair(pairTerm: string): CommercialTermKey[] {
  return pairTerm ? (pairTerm.split(':') as CommercialTermKey[]) : []
}

export const getChoicei18n = getCommercialTermChoiceTranslation(t)

export function getKey(commercialTerm: CommercialTermRow, isTermPaired: boolean) {
  const { key, pair: pairKey } = commercialTerm
  return isTermPaired && pairKey ? pair(key, pairKey) : key
}

export function getOptions(
  commercialTerm: CommercialTermRow,
  isTermPaired: boolean,
  autoSelectSalesOffice: boolean,
  branchOffice: string
): Option[] | undefined {
  const { key, values, pair: pairKey } = commercialTerm

  if (R.isNil(values)) {
    return undefined
  }

  if (isTermPaired) {
    const pairedValues = values as Array<{ key: string; value: string }>
    const dropdownValues = autoSelectSalesOffice
      ? pairedValues.filter(option => branchOffice === option.key)
      : pairedValues

    const finalDropdownValues = dropdownValues.length > 0 ? dropdownValues : pairedValues

    return finalDropdownValues.map(({ key: value, value: pairValue }) => {
      const label = getChoicei18n(key, value) || value
      const pairLabel = getChoicei18n(pairKey!, pairValue) || pairValue
      return {
        key: pair(value, pairValue),
        label: `${pairLabel} (${label})`,
        value: pair(value, pairValue)
      }
    })
  }

  const plainValues = values as string[]
  return plainValues.map((value: string) => ({
    key: value,
    label: getChoicei18n(key, value) || value,
    value
  }))
}
export function getValuesOnContractStartDateChange(
  frontlineConfig: ClientFrontlineConfig,
  changedValue: string | undefined | null
) {
  if (!changedValue) {
    return {
      contractStartDate: undefined,
      escalationDate: undefined
    }
  }
  const escalationDate = getEscalationStartDate(frontlineConfig, changedValue)
  return {
    contractStartDate: changedValue,
    escalationDate
  }
}

export function getEscalationDateOnChange(
  frontlineConfig: ClientFrontlineConfig,
  changedValue: string | undefined,
  currentContractStartDate: string | undefined
) {
  if (!changedValue) {
    if (!currentContractStartDate) {
      return
    }
    return getEscalationStartDate(frontlineConfig, currentContractStartDate)
  }
  return changedValue
}

export function resetCommercialTerms(
  frontlineConfig: FrontlineConfigJson,
  defaultCommercialTerms: CommercialTerms,
  opportunity: Opportunity,
  isRefresh: boolean
) {
  const terms = { ...defaultCommercialTerms }
  const { branchOffice, contractSpecificAgreement, opportunityCategory } = opportunity
  const { autoSelectSalesOffice, commercialTerms } = frontlineConfig
  const vkburCommercialTerm = commercialTerms.find((ct: CommercialTermRow) => ct.key === 'VKBUR')
  const augruCommercialTermValue = commercialTerms.find((ct: CommercialTermRow) => ct.key === 'AUGRU')?.values
  if (autoSelectSalesOffice && branchOffice && vkburCommercialTerm && vkburCommercialTerm.type === 'pair-select') {
    const options = vkburCommercialTerm.values as Array<{ key: string; value: string }>
    const matchingOptions = options.filter(option => branchOffice === option.key)
    if (matchingOptions.length === 1) {
      terms.VKBUR = matchingOptions[0].key
      terms.VKGRP = matchingOptions[0].value
    }
  }

  const renegoDefaultAtEqLevel = typeof terms.AUGRU === 'object' && isRefresh

  if (
    isRenegoOpp(opportunityCategory) &&
    augruCommercialTermValue &&
    augruCommercialTermValue.includes('320') &&
    !renegoDefaultAtEqLevel
  ) {
    terms.AUGRU = '320'
  }

  if (isContractAgreementEnabled(frontlineConfig.commercialTerms)) {
    terms.VBAK_BNAME = contractSpecificAgreement
  }
  return terms
}

const isContractAgreementEnabled = (commercialTermsConfig: CommercialTermRow[]) =>
  commercialTermsConfig.find(terms => terms.key === 'VBAK_BNAME')

export const multiSelectCommercialTerms = ['KCSM_INV_ATTACH']

export const isMultiSelectCommercialTerm = (key: string) => multiSelectCommercialTerms.includes(key)

export const notApplicableValues = ['00']

export const getFullValForMultiSelect = (fullVal: string | string[]) => {
  if (Array.isArray(fullVal) && fullVal[fullVal.length - 1] === notApplicableValues[0]) {
    fullVal = notApplicableValues
  } else if (Array.isArray(fullVal) && fullVal[0] === notApplicableValues[0]) {
    fullVal.shift()
  }
  return fullVal
}

export const validateContractDates = (contractEndDateKey: CommercialTermKey | '', offer: Offer) => {
  const commercialTerms = offer.commercialTerms
  const equipmentCount = offer.buildings.reduce((acc, building) => acc + building.details.equipment.length, 0)

  const contractStartDates = Object.values((commercialTerms.VBEGDAT as EquipmentDate) || {}).filter(v => v != null)
  const contractEndDates = contractEndDateKey
    ? Object.values((commercialTerms[contractEndDateKey] as EquipmentDate) || {}).filter(v => v != null)
    : []

  const uniqueContractStartDates = contractStartDates.filter(
    (value: string, index: number, array: string[]) => array.indexOf(value) === index
  )
  const uniqueEndStartDates = contractEndDates.filter(
    (value: string, index: number, array: string[]) => array.indexOf(value) === index
  )

  const isAllContractDatesSet = contractEndDateKey
    ? contractStartDates.length === equipmentCount && contractEndDates.length === equipmentCount
    : contractStartDates.length === equipmentCount
  return (
    !isAllContractDatesSet ||
    !(contractEndDateKey
      ? uniqueContractStartDates.length === 1 && uniqueEndStartDates.length === 1
      : uniqueContractStartDates.length === 1)
  )
}

const setCommercialTermDates =
  (key: CommercialTermKey, value: string | string[] | undefined, equipmentIDs: string[]) =>
  (offer: Offer): Offer => {
    const commercialTermValue: EquipmentDate = offer.commercialTerms[key] as EquipmentDate
    const updatedCommericalTermValue = equipmentIDs.reduce(
      (ctv, equipmentId) =>
        ({
          ...ctv,
          [equipmentId]: value
        }) as EquipmentDate,
      commercialTermValue
    )

    return {
      ...offer,
      commercialTerms: {
        ...offer.commercialTerms,
        [key]: updatedCommericalTermValue
      }
    }
  }

export const hasCommercialTermPriceConfigured = (commercialTerms: CommercialTermRow[], key: keyof CommercialTerms) =>
  commercialTerms.some(ct => ct.key === key && ct.has_pricing_impact)

const setCommercialTermOrderReasonBillingPlan =
  (key: CommercialTermKey, value: string | undefined, equipmentIDs: string[]) =>
  (offer: Offer, frontlineConfig: ClientFrontlineConfig): Offer => {
    const commercialTermValue: EquipmentDate =
      typeof offer.commercialTerms[key] === 'object' ? (offer.commercialTerms[key] as EquipmentDate) : {}
    const { commercialTerms } = frontlineConfig
    const updatedCommericalTermValue = equipmentIDs.reduce(
      (ctv, equipmentId) => ({
        ...ctv,
        [equipmentId]: value as string
      }),
      commercialTermValue
    )

    return {
      ...offer,
      priceEstimation: hasCommercialTermPriceConfigured(commercialTerms, key) ? undefined : offer.priceEstimation,
      commercialTerms: {
        ...offer.commercialTerms,
        [key]: updatedCommericalTermValue
      }
    }
  }

export const setContractStartDate = (
  date: string | null,
  salesToolId: string[],
  frontlineConfig: ClientFrontlineConfig,
  dispatch: any
) => {
  const isEscalationDateConfigured =
    frontlineConfig.commercialTerms.filter(ct => ct.key === 'KCSM_BASE_MONTH_ESCALATION').length === 1

  if (isEscalationDateConfigured) {
    const { contractStartDate, escalationDate } =
      date !== null
        ? getValuesOnContractStartDateChange(frontlineConfig, date)
        : {
            contractStartDate: undefined,
            escalationDate: undefined
          }
    dispatch(
      updateOfferAsync(
        R.pipe(
          setCommercialTermDates('VBEGDAT', contractStartDate, salesToolId),
          setCommercialTermDates('KCSM_BASE_MONTH_ESCALATION', escalationDate, salesToolId)
        )
      )
    )
  } else {
    dispatch(updateOfferAsync(setCommercialTermDates('VBEGDAT', date ? date : undefined, salesToolId)))
  }
}

export const setContractEndDate = (
  dispatch: any,
  date: string | null,
  salesToolId: string[],
  contractEndDateKey: CommercialTermKey
) => {
  dispatch(
    updateOfferAsync(
      R.pipe(
        setCommercialTermDates(contractEndDateKey as CommercialTermKey, date as string, salesToolId),
        setOfferPriceEstimation
      )
    )
  )
}

export const setBillingOrOrderReason = (
  dispatch: any,
  value: string | null,
  salesToolId: string[],
  key: CommercialTermKey,
  frontlineConfig: ClientFrontlineConfig
) => {
  dispatch(
    updateOfferAsync(
      R.pipe(
        (o: Offer) =>
          setCommercialTermOrderReasonBillingPlan(
            key as CommercialTermKey,
            value as string,
            salesToolId
          )(o, frontlineConfig),
        setOfferPriceEstimation
      )
    )
  )
}

export const getIsBillingModeApplicable = (
  equipmentGroups: EquipmentGroups
): boolean => {
  if (!equipmentGroups || typeof equipmentGroups !== 'object') {
    return false
  }

  const vasServiceKeys = [
    'KCSM_SN_IMMEDIATE',
    'KCSM_SN_STANDARD',
    'KCSM_REMOTE_BREAKDOWN',
    'KCSM_REMOTE_RESCUE',
    'KCSM_REMOTE_MAINT',
    'KCSM_INFOTAINMENT',
    'KCSM_LCP',
    'KCSM_AIR_PURIFIER',
    'KCSM_APF_ELEV_CALL_API',
    'KCSM_APF_EQUIP_STAT_API',
    'KCSM_MOBILE_ELEV_CALL',
    'KCSM_APF_SERV_INFO_API',
    'KCSM_APF_SERV_ROBOT_API'
  ]

  const isValidValue = (value: any): boolean =>
    value !== undefined &&
    value !== null &&
    value !== '0' &&
    value !== 'NA' &&
    !(Array.isArray(value) && value.length === 1 && value[0] === '0')

  const getValidVasSelections = (selections: any): string[] =>
    Object.keys(selections).reduce((vasSelections: any, key: string) => {
      if (vasServiceKeys.includes(key) && isValidValue(selections[key])) {
        return vasSelections.concat(key)
      }
      return vasSelections
    }, [])

  return Object.values(equipmentGroups).reduce((isFilteredObjEmpty, equipmentGroup) => {
    if (equipmentGroup.groupKind === 'elevators_and_escalators') {
      const elevatorSelections = equipmentGroup.elevator ? equipmentGroup.elevator?.selections : {}
      const escalatorSelections = equipmentGroup.escalator ? equipmentGroup.escalator?.selections : {}
      const validElevatorVasSelections = getValidVasSelections(elevatorSelections)
      const validEscalatorVasSelections = getValidVasSelections(escalatorSelections)
      if (validElevatorVasSelections.length || validEscalatorVasSelections.length) {
        return true
      }
    }
    return isFilteredObjEmpty
  }, false)
}

export const isKCSM_BILLING_PLAN = (key: string) => key === 'KCSM_BILLING_PLAN'

export const isAUGRU = (key: string) => key === 'AUGRU'

export const getDefaultCommercialTerms = (
  frontlineConfig: FrontlineConfigJson,
  opportunity: Opportunity
): CommercialTerms => {
  const defaultCommercialTerms = frontlineConfig.commercialTerms.reduce((acc, term) => {
    if (term.is_multiselect && !R.isNil(term.multiselect_default_values)) {
      return R.assoc(term.key, term.multiselect_default_values, acc)
    }
    if (!R.isNil(term.default_value)) {
      return isMultiSelectCommercialTerm(term.key)
        ? R.assoc(term.key, [term.default_value], acc)
        : R.assoc(term.key, term.default_value, acc)
    }
    return acc
  }, {} as CommercialTerms)

  return resetCommercialTerms(frontlineConfig, defaultCommercialTerms, opportunity, false)
}

export interface CommercialTermsConfigAndErrors {
  filteredCommercialTermsConfig: CommercialTermRow[]
  commercialTermsErrors: PRecord<CommercialTermKey, never[]>
  isBillingModeApplied: boolean
}

export function getCommercialTermsConfigAndErrors(
  frontLineConfig: ClientFrontlineConfig | undefined,
  currentOffer: Offer | undefined,
  commercialTerms: CommercialTerms | undefined,
  isContractStartDateAtEquipmentLevel: boolean | undefined,
  isBillingPlanAtEquipmentLevel: boolean | undefined,
  isOrderReasonAtEquipmentLevel: boolean | undefined
): CommercialTermsConfigAndErrors {
  const commercialTermsConfig = frontLineConfig?.commercialTerms

  const isBillingModeApplied =
    currentOffer && frontLineConfig ? getIsBillingModeApplicable(currentOffer.equipmentGroups, frontLineConfig) : false

  const filteredCommercialTermsConfig =
    commercialTermsConfig?.filter(
      (term: any) => isBillingModeApplied || !COMMERCIAL_TERMS_TO_FILTER.includes(term.key) // TODO: Filtered FP and PP as not part of MVP
    ) || []

  const commercialTermsErrors: PRecord<CommercialTermKey, never[]> =
    currentOffer && filteredCommercialTermsConfig.length > 0
      ? getCommercialTermsErrors({
          commercialTermsConfig: filteredCommercialTermsConfig.filter((term: any) => {
            if (
              term.type === 'date' &&
              currentOffer.commercialTerms &&
              currentOffer.commercialTerms[term.key as keyof CommercialTerms]
            ) {
              return true
            }
            return term.optional === false
          }), // Only validate mandatory commercial terms
          selectedCommercialTerms: commercialTerms || {},
          isContractStartDateAtEquipmentLevel: isContractStartDateAtEquipmentLevel || false,
          isBillingModeApplied,
          isBillingPlanAtEquipmentLevel: isBillingPlanAtEquipmentLevel || false,
          isOrderReasonAtEquipmentLevel: isOrderReasonAtEquipmentLevel || false
        })
      : {}

  return {
    filteredCommercialTermsConfig,
    commercialTermsErrors,
    isBillingModeApplied
  }
}

export const mayHavePricingEffect = (key: CommercialTermKey): boolean =>
  !!(commercialTermsWithPricing as Record<CommercialTermKey, boolean>)[key] || isExtraCommercialTerm(key)

export const updatePriceEstimation = (key: string): boolean =>
  PRICING_IMPACT_TERMS.includes(key) || mayHavePricingEffect(key as CommercialTermKey)
